import type { ListingsResponse, RiskSettingsResponse, SymbolVetoDto, UniverseConfig, UniverseResponse } from '@et/shared';
import { useEffect, useState } from 'react';
import { api, describeError, symbolPath } from '../api/client';
import { Coin } from '../components/CoinLogo';
import { dateTime } from '../lib/format';
import { t, useT } from '../lib/i18n';
import { toast } from '../lib/toast';
import { useResource } from '../lib/use-resource';

const UNIVERSE_FIELDS: { key: keyof UniverseConfig; label: string; hint: string; int?: boolean }[] = [
  { key: 'topN', label: 'How many coins to trade', hint: 'The most traded USDT pairs on Binance, by 24h volume.', int: true },
  { key: 'minQuoteVolume', label: 'Minimum 24h volume (USDT)', hint: 'Coins trading less than this are skipped: thin markets move erratically.' },
  { key: 'newListingDays', label: 'Watch new listings for (days)', hint: 'A coin Binance just opened is added regardless of volume. 0 = off.', int: true },
  { key: 'refreshMinutes', label: 'Re-rank every (min)', hint: 'How often the ranking is rebuilt.', int: true },
];

const LISTING_STATUS: Record<string, string> = { upcoming: 'Coming soon', trading: 'Trading', removed: 'Removed' };

const RISK_FIELDS: { key: keyof RiskSettingsResponse; label: string; int?: boolean }[] = [
  { key: 'maxConcurrentTrades', label: 'Max concurrent trades', int: true },
  { key: 'maxDailyDrawdownPct', label: 'Max daily drawdown %' },
  { key: 'basePositionPct', label: 'Base position % of equity' },
  { key: 'maxPositionPct', label: 'Max position %' },
  { key: 'minConfidence', label: 'Min confidence (0–1)' },
  { key: 'cooldownAfterLossMin', label: 'Cooldown after loss (min)', int: true },
];

async function attempt(fn: () => Promise<unknown>, ok: string, after?: () => void): Promise<void> {
  try {
    await fn();
    toast.success(ok);
    after?.();
  } catch (err) {
    toast.error(describeError(err));
  }
}

export function Risk(): JSX.Element {
  const risk = useResource<RiskSettingsResponse>('/risk');
  const vetoes = useResource<SymbolVetoDto[]>('/risk/vetoes');
  const universe = useResource<UniverseResponse>('/universe');
  const listings = useResource<ListingsResponse>('/universe/listings');
  const [form, setForm] = useState<Record<string, string>>({});
  const [uForm, setUForm] = useState<Record<string, string>>({});
  const [symbol, setSymbol] = useState('');
  const tr = useT();

  useEffect(() => {
    if (risk.data) setForm(Object.fromEntries(RISK_FIELDS.map((f) => [f.key, String(risk.data?.[f.key] ?? '')])));
  }, [risk.data]);

  useEffect(() => {
    const c = universe.data?.config;
    if (c) setUForm(Object.fromEntries(UNIVERSE_FIELDS.map((f) => [f.key, String(c[f.key])])));
  }, [universe.data]);

  const saveUniverse = (): Promise<void> =>
    attempt(
      () => api.put('/universe/config', { ...universe.data?.config, ...Object.fromEntries(UNIVERSE_FIELDS.map((f) => [f.key, f.int ? Number(uForm[f.key]) : uForm[f.key]])) }),
      t('Coin list settings saved — applied on the next ranking'),
      () => void universe.reload(),
    );

  const saveRisk = (): Promise<void> =>
    attempt(() => api.put('/risk', Object.fromEntries(RISK_FIELDS.map((f) => [f.key, f.int ? Number(form[f.key]) : form[f.key]]))), t('Risk settings saved'), () => void risk.reload());

  const add = (list: 'whitelist' | 'blacklist'): Promise<void> =>
    attempt(() => api.post(`/universe/${list}`, { symbol: symbol.trim().toUpperCase() }), t('{symbol} added to {list}', { symbol, list: t(list) }), () => { setSymbol(''); void universe.reload(); });

  return (
    <div className="stack">
      <h1>{tr('Risk')}</h1>
      <div className="card stack">
        <div className="form-grid">
          {RISK_FIELDS.map((f) => (
            <label key={f.key} className="field">{tr(f.label)}<input className="mono" value={form[f.key] ?? ''} onChange={(e) => { setForm({ ...form, [f.key]: e.target.value }); }} /></label>
          ))}
        </div>
        <div><button className="primary" onClick={() => void saveRisk()}>{tr('Save')}</button></div>
      </div>

      <div className="card">
        <h2>{tr('Symbol vetoes')}</h2>
        <p className="muted" style={{ marginTop: 0 }}>{tr('Set automatically by Binance delisting announcements. They never expire on their own.')}</p>
        {vetoes.data?.length === 0 && <div className="empty">{tr('No vetoes.')}</div>}
        <div className="table-wrap"><table><tbody>{vetoes.data?.map((v) => (
          <tr key={v.symbol}><td><Coin symbol={v.symbol} /></td><td className="muted" style={{ whiteSpace: 'normal' }}>{v.newsTitle}</td><td className="muted">{dateTime(v.at)}</td>
            <td><button className="small" onClick={() => void attempt(() => api.del(`/risk/vetoes/${symbolPath(v.symbol)}`), t('Veto on {symbol} cleared', { symbol: v.symbol }), () => void vetoes.reload())}>{tr('Clear')}</button></td></tr>
        ))}</tbody></table></div>
      </div>

      <h1 style={{ marginTop: 16 }}>{tr('Asset universe')}</h1>
      <div className="card stack">
        <div className="row">
          <input placeholder="SOL/USDT" value={symbol} onChange={(e) => { setSymbol(e.target.value); }} />
          <button onClick={() => void add('whitelist')} disabled={!symbol}>{tr('Whitelist')}</button>
          <button onClick={() => void add('blacklist')} disabled={!symbol}>{tr('Blacklist')}</button>
          <span style={{ flex: 1 }} />
          <button onClick={() => void attempt(() => api.post('/universe/rescan'), t('Universe rescanned'), () => void universe.reload())}>{tr('Rescan now')}</button>
        </div>
        {universe.data && (
          <>
            <div className="form-grid">
              {UNIVERSE_FIELDS.map((f) => (
                <label key={f.key} className="field">
                  {tr(f.label)}
                  <input className="mono" inputMode="numeric" value={uForm[f.key] ?? ''} onChange={(e) => { setUForm({ ...uForm, [f.key]: e.target.value }); }} />
                  <span className="hint">{tr(f.hint)}</span>
                </label>
              ))}
            </div>
            <div><button className="primary" onClick={() => void saveUniverse()}>{tr('Save')}</button></div>
            <div className="muted">
              {tr('Binance has {n} USDT pairs; all are ranked every {min} min and the top {top} above {vol} USDT volume are traded.', { n: listings.data?.trackedMarkets ?? '—', min: universe.data.config.refreshMinutes, top: universe.data.config.topN, vol: universe.data.config.minQuoteVolume })}
            </div>
            <div><b>{tr('Active ({n}):', { n: universe.data.active.length })}</b></div>
            <div className="coin-grid">{universe.data.active.map((s) => <Coin key={s} symbol={s} />)}</div>
            <div className="row"><b>{tr('Whitelist')}:</b>{universe.data.whitelist.map((s) => <span key={s} className="chip">{s} <button className="small" onClick={() => void attempt(() => api.del(`/universe/whitelist/${symbolPath(s)}`), t('{symbol} removed', { symbol: s }), () => void universe.reload())}>×</button></span>)}</div>
            <div className="row"><b>{tr('Blacklist')}:</b>{universe.data.blacklist.map((s) => <span key={s} className="chip">{s} <button className="small" onClick={() => void attempt(() => api.del(`/universe/blacklist/${symbolPath(s)}`), t('{symbol} removed', { symbol: s }), () => void universe.reload())}>×</button></span>)}</div>
          </>
        )}
      </div>

      <h1 style={{ marginTop: 16 }}>{tr('New listings on Binance')}</h1>
      <div className="card stack">
        <div className="muted">
          {tr('The full Binance market list is checked every {sec} seconds. A new USDT pair is announced on Telegram and watched for {days} days.', { sec: universe.data?.config.listingPollSec ?? 60, days: universe.data?.config.newListingDays ?? 3 })}
          {listings.data?.lastCheckedAt && <> {tr('Last check: {time}.', { time: dateTime(listings.data.lastCheckedAt) })}</>}
        </div>
        {listings.data && listings.data.items.length === 0 && <div className="empty">{tr('No new listings in the last 30 days since tracking started.')}</div>}
        {listings.data && listings.data.items.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead><tr><th>{tr('Coin')}</th><th>{tr('Status')}</th><th>{tr('First seen')}</th><th>{tr('Trading since')}</th><th>{tr('Bot watching')}</th></tr></thead>
              <tbody>
                {listings.data.items.map((l) => (
                  <tr key={l.symbol}>
                    <td><Coin symbol={l.symbol} /></td>
                    <td><span className={`chip ${l.status === 'trading' ? 'good' : ''}`}>{tr(LISTING_STATUS[l.status] ?? l.status)}</span></td>
                    <td className="muted">{dateTime(l.firstSeenAt)}</td>
                    <td className="muted">{dateTime(l.listedAt)}</td>
                    <td>{l.inUniverse ? tr('Yes') : tr('No')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
